import React from 'react';
import styled from 'styled-components';

/** The level a dub plays at when nobody has turned it down. */
export const FULL_DUB_LEVEL = 100;

/** PLAT-11927 — the two levels a dub mix has, each 0–100. */
export interface DubLevels {
  /** The dubbed voice. */
  voiceVolume: number;
  /** The original recording under it, on Both. */
  originalVolume: number;
}

/** "40%", or "Muted" at 0 — the one readout every Dub Level control shows. */
export function formatDubLevel(level: number): string {
  return level === 0 ? 'Muted' : `${level}%`;
}

interface DubLevelSliderProps {
  id?: string;
  /** 0–100. */
  value: number;
  /** Every step of a drag or key press — what is HEARD. */
  onChange: (level: number) => void;
  /**
   * The pointer or key is let go — what is KEPT. A host that saves the level
   * saves it here, so one drag is one save and one undo step.
   */
  onCommit?: (level: number) => void;
  ariaLabel?: string;
  width?: number;
}

/**
 * The Dub Level slider (PLAT-11899): gold, like every visual/creative control
 * (design-system.md). Double-click resets to full.
 */
export const DubLevelSlider: React.FC<DubLevelSliderProps> = ({
  id,
  value,
  onChange,
  onCommit,
  ariaLabel = 'Dub level',
  width = 150,
}) => {
  const commit = (event: React.SyntheticEvent<HTMLInputElement>) =>
    onCommit?.(Number(event.currentTarget.value));

  return (
    <Range
      id={id}
      type="range"
      min={0}
      max={FULL_DUB_LEVEL}
      step={1}
      value={value}
      aria-label={ariaLabel}
      aria-valuetext={formatDubLevel(value)}
      title="Double-click to reset to 100%"
      $fill={value}
      $width={width}
      onChange={event => onChange(Number(event.currentTarget.value))}
      onPointerUp={commit}
      onKeyUp={commit}
      onDoubleClick={() => {
        onChange(FULL_DUB_LEVEL);
        onCommit?.(FULL_DUB_LEVEL);
      }}
    />
  );
};

const Range = styled.input<{ $fill: number; $width: number }>`
  -webkit-appearance: none;
  appearance: none;
  width: ${({ $width }) => $width}px;
  height: 4px;
  margin: 0;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  background: linear-gradient(
    90deg,
    var(--accent) ${({ $fill }) => $fill}%,
    var(--border) ${({ $fill }) => $fill}%
  );

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: var(--accent);
    border: 2px solid var(--surface);
  }
  &::-moz-range-thumb {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--accent);
    border: 2px solid var(--surface);
  }
  &:focus-visible::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px var(--accent-tint-strong);
  }
`;
